
<script setup lang="ts">
const meta = {"nav":"Lists","title":"List component","description":"The list component is a continuous group of text, images and icons that may contain primary or supplemental actions.","keywords":"lists, vuetify list component, vue list component"}
 const assets = undefined
 const backmatter = undefined
 const features = {"figma":true,"github":"/components/VList/","label":"C: VList","report":true,"spec":"https://m2.material.io/components/lists"}
 const fluid = undefined
 const related = ["/components/item-groups/","/components/avatars/","/components/sheets/"]
 const toc = [{"text":"Usage","to":"#usage","level":2},{"text":"API","to":"#api","level":2},{"text":"Examples","to":"#examples","level":2},{"text":"Props","to":"#props","level":3},{"text":"Items","to":"#items","level":4},{"text":"Density","to":"#density","level":4},{"text":"Disabled","to":"#disabled","level":4},{"text":"Variant","to":"#variant","level":4},{"text":"Nav","to":"#nav","level":4},{"text":"Rounded","to":"#rounded","level":4},{"text":"Shaped","to":"#shaped","level":4},{"text":"Sub group","to":"#sub-group","level":4},{"text":"Three line","to":"#three-line","level":4},{"text":"Two lines and subheader","to":"#two-lines-and-subheader","level":4},{"text":"Misc","to":"#misc","level":3},{"text":"Action and item groups","to":"#action-and-item-groups","level":4},{"text":"Action with text","to":"#action-with-text","level":4},{"text":"Card list","to":"#card-list","level":4}]
  defineExpose({ frontmatter: {"meta":{"nav":"Lists","title":"List component","description":"The list component is a continuous group of text, images and icons that may contain primary or supplemental actions.","keywords":"lists, vuetify list component, vue list component"},"features":{"figma":true,"github":"/components/VList/","label":"C: VList","report":true,"spec":"https://m2.material.io/components/lists"},"related":["/components/item-groups/","/components/avatars/","/components/sheets/"],"toc":[{"text":"Usage","to":"#usage","level":2},{"text":"API","to":"#api","level":2},{"text":"Examples","to":"#examples","level":2},{"text":"Props","to":"#props","level":3},{"text":"Items","to":"#items","level":4},{"text":"Density","to":"#density","level":4},{"text":"Disabled","to":"#disabled","level":4},{"text":"Variant","to":"#variant","level":4},{"text":"Nav","to":"#nav","level":4},{"text":"Rounded","to":"#rounded","level":4},{"text":"Shaped","to":"#shaped","level":4},{"text":"Sub group","to":"#sub-group","level":4},{"text":"Three line","to":"#three-line","level":4},{"text":"Two lines and subheader","to":"#two-lines-and-subheader","level":4},{"text":"Misc","to":"#misc","level":3},{"text":"Action and item groups","to":"#action-and-item-groups","level":4},{"text":"Action with text","to":"#action-with-text","level":4},{"text":"Card list","to":"#card-list","level":4}]}, excerpt: undefined })
</script>
<script lang="ts">
/** frontmatter meta-data for MD page **/
export interface Frontmatter {
  title?: string
  description?: string
  subject?: string
  category?: string
  name?: string
  excerpt?: string
  image?: string
  layout?: string
  requiredAuth?: boolean
  meta?: Record<string, any>[]
  [key: string]: unknown
}
export const frontmatter: Frontmatter = {"meta":{"nav":"Lists","title":"List component","description":"The list component is a continuous group of text, images and icons that may contain primary or supplemental actions.","keywords":"lists, vuetify list component, vue list component"},"features":{"figma":true,"github":"/components/VList/","label":"C: VList","report":true,"spec":"https://m2.material.io/components/lists"},"related":["/components/item-groups/","/components/avatars/","/components/sheets/"],"toc":[{"text":"Usage","to":"#usage","level":2},{"text":"API","to":"#api","level":2},{"text":"Examples","to":"#examples","level":2},{"text":"Props","to":"#props","level":3},{"text":"Items","to":"#items","level":4},{"text":"Density","to":"#density","level":4},{"text":"Disabled","to":"#disabled","level":4},{"text":"Variant","to":"#variant","level":4},{"text":"Nav","to":"#nav","level":4},{"text":"Rounded","to":"#rounded","level":4},{"text":"Shaped","to":"#shaped","level":4},{"text":"Sub group","to":"#sub-group","level":4},{"text":"Three line","to":"#three-line","level":4},{"text":"Two lines and subheader","to":"#two-lines-and-subheader","level":4},{"text":"Misc","to":"#misc","level":3},{"text":"Action and item groups","to":"#action-and-item-groups","level":4},{"text":"Action with text","to":"#action-with-text","level":4},{"text":"Card list","to":"#card-list","level":4}]}
export const excerpt: string | undefined = ""

</script>
<template>
<div><section id="lists">
<app-heading href="#lists" level="1">Lists</app-heading>
<p>The <v-code>v-list</v-code> component is used to display information. It can contain an avatar, content, actions, subheaders and much more. Lists present content in a way that makes it easy to identify a specific item in a collection. They provide a consistent styling for organizing groups of text and images.</p>
<page-features></page-features>
<section id="usage">
<app-heading href="#usage" level="2">Usage</app-heading>
<p>Lists come in three main variations. <strong>single-line</strong> (default), <strong>two-line</strong> and <strong>three-line</strong>. The line declaration specifies the minimum height of the item and can also be controlled from <v-code>v-list</v-code> with the same prop.</p>
<examples-usage name="v-list"></examples-usage>
<promoted-entry></promoted-entry>
</section>
<section id="api">
<app-heading href="#api" level="2">API</app-heading>
<app-table>
<thead>
<tr>
<th>Component</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><app-link href="/api/v-list/">v-list</app-link></td>
<td>Primary Component</td>
</tr>
<tr>
<td><app-link href="/api/v-list-group/">v-list-group</app-link></td>
<td>Sub-component used to display or hide groups of items</td>
</tr>
<tr>
<td><app-link href="/api/v-list-subheader/">v-list-subheader</app-link></td>
<td>Sub-component used to separate groups of items</td>
</tr>
<tr>
<td><app-link href="/api/v-list-item/">v-list-item</app-link></td>
<td>Sub-component used to display a single item or modify the <v-code>v-list</v-code> state</td>
</tr>
<tr>
<td><app-link href="/api/v-list-item-title/">v-list-item-title</app-link></td>
<td>Sub-component used to display the title of a list item. Wraps the <v-code>#title</v-code> slot</td>
</tr>
<tr>
<td><app-link href="/api/v-list-item-subtitle/">v-list-item-subtitle</app-link></td>
<td>Sub-component used to display the subtitle of a list item. Wraps the <v-code>#subtitle</v-code> slot</td>
</tr>
<tr>
<td><app-link href="/api/v-list-item-action/">v-list-item-action</app-link></td>
<td>Sub-component used to display <app-link href="/components/checkboxes/">v-checkbox</app-link> or <app-link href="/components/switches/">v-switch</app-link></td>
</tr>
<tr>
<td><app-link href="/api/v-list-img/">v-list-img</app-link></td>
<td>Sub-component that is used to wrap a the <app-link href="/components/images/">v-img</app-link> component</td>
</tr>
<tr>
<td><app-link href="/api/v-list-item-media/">v-list-item-media</app-link></td>
<td>Sub-component that is used to wrap a the <app-link href="/components/images/">v-img</app-link> component</td>
</tr>
</tbody>
</app-table>
<api-inline hide-links=""></api-inline>
</section>
<section id="examples">
<app-heading href="#examples" level="2">Examples</app-heading>
<section id="props">
<app-heading href="#props" level="3">Props</app-heading>
<section id="items">
<app-heading href="#items" level="4">Items</app-heading>
<p>Lists can either be created by markup using the many sub-components that are available, or by using the <strong>items</strong> prop.</p>
<examples-example file="v-list/prop-items"></examples-example>
<p>To customize which properties will be used for the title and value of each item, use the <strong>item-title</strong> and <strong>item-value</strong> props.</p>
<examples-example file="v-list/prop-items-custom"></examples-example>
<p>If you need to render subheaders or dividers, add an item with a <strong>type</strong> property. Which property to use can be customized using the <strong>item-type</strong> prop.</p>
<examples-example file="v-list/prop-items-type"></examples-example>
<p>To customize individual items, you can use the <strong>item-props</strong> prop. It defaults to looking for a <strong>props</strong> property on the items. The value should be an object, and if found it will be spread on the <strong>v-list-item</strong> component.</p>
<p>If <strong>item-props</strong> is set to <strong>true</strong> then the whole item will be spread.</p>
<examples-example file="v-list/prop-items-prop"></examples-example>
</section>
<section id="density">
<app-heading href="#density" level="4">Density</app-heading>
<p><v-code>v-list</v-code> supports the <strong>density</strong> property.</p>
<examples-example file="v-list/prop-density"></examples-example>
<promoted-promoted slug="vuetify-lux-admin-pro"></promoted-promoted>
</section>
<section id="disabled">
<app-heading href="#disabled" level="4">Disabled</app-heading>
<p>You cannot interact with disabled <v-code>v-list</v-code>.</p>
<examples-example file="v-list/prop-disabled"></examples-example>
</section>
<section id="variant">
<app-heading href="#variant" level="4">Variant</app-heading>
<p><v-code>v-list</v-code> supports the <strong>variant</strong> prop.</p>
<examples-example file="v-list/prop-variant"></examples-example>
</section>
<section id="nav">
<app-heading href="#nav" level="4">Nav</app-heading>
<p>Lists can receive an alternative <strong>nav</strong> styling that reduces the width <v-code>v-list-item</v-code> takes up as well as adding a border radius.</p>
<examples-example file="v-list/prop-nav"></examples-example>
</section>
<section id="rounded">
<app-heading href="#rounded" level="4">Rounded</app-heading>
<p>You can make <v-code>v-list</v-code> items rounded.</p>
<examples-example file="v-list/prop-rounded"></examples-example>
</section>
<section id="shaped">
<app-heading href="#shaped" level="4">Shaped</app-heading>
<p>Shaped lists have rounded borders on one side of the <v-code>v-list-item</v-code>.</p>
<examples-example file="v-list/prop-shaped"></examples-example>
</section>
<section id="sub-group">
<app-heading href="#sub-group" level="4">Sub group</app-heading>
<p>Using the <v-code>v-list-group</v-code> component you can create sub-groups of items.</p>
<examples-example file="v-list/prop-sub-group"></examples-example>
</section>
<section id="three-line">
<app-heading href="#three-line" level="4">Three line</app-heading>
<p>For three line lists, the subtitle will clamp vertically at 2 lines and then ellipsis. This feature uses <app-link href="https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp">line-clamp</app-link> and is not supported in all browsers.</p>
<examples-example file="v-list/prop-three-line"></examples-example>
</section>
<section id="two-lines-and-subheader">
<app-heading href="#two-lines-and-subheader" level="4">Two lines and subheader</app-heading>
<p>Lists can contain subheaders, dividers, and can contain 1 or more lines. The subtitle will overflow with ellipsis if it extends past one line.</p>
<examples-example file="v-list/prop-two-line-and-subheader"></examples-example>
</section>
</section>
<section id="misc">
<app-heading href="#misc" level="3">Misc</app-heading>
<section id="action-and-item-groups">
<app-heading href="#action-and-item-groups" level="4">Action and item groups</app-heading>
<p>A <strong>three-line</strong> list with actions. Utilizing <strong>select-strategy</strong>, easily connect actions to your tiles.</p>
<examples-example file="v-list/misc-actions"></examples-example>
</section>
<section id="action-with-text">
<app-heading href="#action-with-text" level="4">Action with text</app-heading>
<p>A list can contain additional meta information within an action.</p>
<examples-example file="v-list/misc-action-stack"></examples-example>
</section>
<section id="card-list">
<app-heading href="#card-list" level="4">Card list</app-heading>
<p>A list can be combined with a card.</p>
<examples-example file="v-list/misc-card-list"></examples-example>
</section>
</section>
</section>
</section>
</div>
</template>
